/**
 * Heuristic mapping from CSV headers to Okta user profile keys (camelCase).
 */
const ALIASES = {
  firstName: ["firstname", "first name", "first_name", "givenname", "given name", "fname"],
  lastName: ["lastname", "last name", "last_name", "surname", "family name", "lname"],
  email: ["email", "e-mail", "email address", "mail", "userprincipalname", "upn", "work email"],
  login: ["login", "username", "user name", "userid", "user id", "sign on name"],
  title: ["title", "job title"],
  mobilePhone: ["mobile", "mobile phone", "cell", "phone", "telephone"],
  department: ["department", "dept", "division"],
};

function norm(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * @param {string[]} headers
 * @returns {Record<string, number>} profile key → column index
 */
export function mapHeadersToProfileKeys(headers) {
  const byNorm = new Map();
  headers.forEach((h, i) => {
    const n = norm(h);
    if (n && !byNorm.has(n)) byNorm.set(n, i);
  });

  const mapping = {};
  for (const [key, aliases] of Object.entries(ALIASES)) {
    for (const a of aliases) {
      const idx = byNorm.get(a);
      if (idx !== undefined) {
        mapping[key] = idx;
        break;
      }
    }
  }

  if (mapping.email === undefined) {
    for (let i = 0; i < headers.length; i++) {
      const n = norm(headers[i]);
      if (n.includes("email") || n === "mail" || n.endsWith("mail")) {
        mapping.email = i;
        break;
      }
    }
  }

  return mapping;
}

/**
 * Build Okta create-user bodies from CSV rows using mapped columns.
 * Unknown columns are copied into profile using a safe key (alphanumeric only) when possible.
 * @param {string[]} headers
 * @param {string[][]} rows
 * @returns {{ users: { profile: Record<string, string> }[], columnMapping: Record<string, string>, warnings: string[] }}
 */
export function buildUsersFromCsv(headers, rows) {
  const warnings = [];
  const columnMapping = {};
  const keyMapping = mapHeadersToProfileKeys(headers);
  for (const [k, idx] of Object.entries(keyMapping)) {
    columnMapping[headers[idx]] = k;
  }

  const used = new Set(Object.values(keyMapping));

  /** @type {Array<{ header: string, key: string, index: number }>} */
  const extras = [];
  headers.forEach((h, i) => {
    if (used.has(i)) return;
    const base = norm(h).replace(/[^a-z0-9]+/gi, "");
    if (!base || base.length > 100) return;
    const camel = base.charAt(0).toLowerCase() + base.slice(1);
    extras.push({ header: h, key: camel, index: i });
  });

  const users = [];
  for (const row of rows) {
    const profile = {};
    for (const [k, idx] of Object.entries(keyMapping)) {
      const v = row[idx];
      if (v != null && String(v).trim() !== "") profile[k] = String(v).trim();
    }
    if (profile.email && !profile.login) profile.login = profile.email;

    for (const ex of extras) {
      const v = row[ex.index];
      if (v != null && String(v).trim() !== "") profile[ex.key] = String(v).trim();
    }

    if (profile.email || profile.login) {
      users.push({ profile });
    }
  }

  if (!keyMapping.email && !keyMapping.login) {
    warnings.push("No email or login column detected; rows need an email-like column to create users.");
  }
  if (users.length === 0 && rows.length > 0) {
    warnings.push("No rows had a usable email or login value.");
  }

  return { users, columnMapping, warnings };
}
